import { apiRoutes } from '@routes/constants/apiRoutes';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { catchError, Observable, of, tap, throwError, shareReplay, finalize, map, switchMap, EMPTY } from 'rxjs';
import { ILoginResponse } from '@interfaces/loginResponse';
import { IOtpPayload } from '@interfaces/otpPayload';
import { IResetPassword } from '@interfaces/resetPassword';
import { IUserRegister } from '@interfaces/userRegistration';
import { NotificationTypes } from '@enum/notification.enum';
import { NotificationService } from '@services/notification.service';
import { IUserProfile } from '@interfaces/userProfile';
import { isPlatformBrowser } from '@angular/common';
import { IUserProfileUpdate } from '../models/userProfile.model';
import { HttpClientObserveEnum } from '@enum/httpClientObserve.enum';
import { NotificationConstants } from '@app/shared/constants/notificationConstants';
import { LocalStorage } from '../enum/localStorage.enum';
import { InterceptorConstants } from '@app/shared/constants/interceptorConstants';

const { CART_COMMERCIAL } = apiRoutes;

@Injectable({
  providedIn: 'root'
})
export class AuthApiService {
  constructor(
    private httpClient$: HttpClient,
    private notificationService: NotificationService,
    @Inject(PLATFORM_ID) private platformId: object
  ) {}

  private authTokenInflight$?: Observable<HttpResponse<ILoginResponse> | HttpErrorResponse>;
  private userProfileInFlight: Map<string, Observable<HttpResponse<IUserProfile> | HttpErrorResponse>> = new Map();

  // Generic post method with conditional error handling and optional custom error handler
  private postRequest<T, P>(
    url: string,
    payload?: P,
    customError: boolean = true,
    customErrorHandler?: (res: HttpErrorResponse) => void,
    headers?: HttpHeaders
  ): Observable<HttpResponse<T> | HttpErrorResponse> {
    return this.httpClient$
      .post<T>(url, payload, {
        headers,
        observe: HttpClientObserveEnum.RESPONSE
      })
      .pipe(
        catchError((res: HttpErrorResponse) => {
          if (customError) {
            if (customErrorHandler) {
              customErrorHandler(res);
            } else {
              this.notificationService.addNotification(
                res.error?.error_description || res.error?.errors?.[0]?.message,
                NotificationTypes.ERROR
              );
            }
            return throwError(() => res);
          }
          return of(res);
        })
      );
  }

  public getAuthTokenApi$(): Observable<HttpResponse<ILoginResponse> | HttpErrorResponse> {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;

    const url = `${apiRoutes.AUTHORIZATION_BEARER_TOKEN_API}`;
    const body = new HttpParams().set(InterceptorConstants.GRANT_TYPE_KEY, 'client_credentials').set('scope', 'basic');
    const headers = new HttpHeaders({
      'Content-Type': InterceptorConstants.LOGIN_CONTENT_TYPE
    });

    return this.postRequest<ILoginResponse, string>(url, body.toString(), true, undefined, headers).pipe(
      tap((response) => {
        if (response instanceof HttpResponse) {
          const token = response?.body?.access_token;
          if (token) {
            if (isPlatformBrowser(this.platformId)) {
              // Only set access_token if user is not logged in (no commercial access token exists)
              const commercialToken = localStorage.getItem(LocalStorage.COMMERCIAL_ACCESS_TOKEN);
              if (!commercialToken) {
                localStorage.setItem(LocalStorage.ACCESS_TOKEN, token);
              }
            }
          }
        }
      })
    );
  }

  // Coalesce concurrent token calls and share the result
  public getAuthTokenCoalesced$(): Observable<HttpResponse<ILoginResponse> | HttpErrorResponse> {
    if (this.authTokenInflight$) {
      return this.authTokenInflight$;
    }
    const request$ = this.getAuthTokenApi$().pipe(
      shareReplay(1),
      finalize(() => {
        this.authTokenInflight$ = undefined;
      })
    );
    this.authTokenInflight$ = request$;
    return request$;
  }

  public loginUserIn$(
    email: string,
    password: string,
    customError: boolean = true
  ): Observable<HttpResponse<ILoginResponse> | HttpErrorResponse> {
    const url = `${apiRoutes.AUTHORIZATION_BEARER_TOKEN_API}`;
    const body = new HttpParams()
      .set(InterceptorConstants.GRANT_TYPE_KEY, 'mhk_password')
      .set('username', email?.toLowerCase())
      .set('password', password);
    const headers = new HttpHeaders({
      'Content-Type': InterceptorConstants.LOGIN_CONTENT_TYPE
    });

    return this.postRequest<ILoginResponse, string>(url, body.toString(), customError, undefined, headers);
  }

  public forgetPasswordIn$(
    email: string,
    customError: boolean = true
  ): Observable<HttpResponse<void> | HttpErrorResponse> {
    const url = `${apiRoutes.FORGOT_PASSWORD_URL}?userId=${encodeURIComponent(email)}`;
    return this.postRequest<void, null>(url, null, customError, (res) => {
      let errorMessage;
      if (res?.status === 500) {
        errorMessage = NotificationConstants.NO_ACCOUNT_FOUND_FOR_THAT_EMAIL;
        this.notificationService.addNotification(errorMessage, NotificationTypes.ERROR);
      } else {
        this.notificationService.addNotification(
          res.error?.error_description || res.error?.errors?.[0]?.message,
          NotificationTypes.ERROR
        );
      }
    });
  }

  public validateOtpIn$(
    otp: number,
    userId: string,
    customError: boolean = true
  ): Observable<HttpResponse<void> | HttpErrorResponse> {
    const url = `${apiRoutes.VALIDATE_OTP_URL}`;
    const requestPayload: IOtpPayload = {
      otp: otp.toString(),
      userId
    };
    return this.postRequest<void, IOtpPayload>(url, requestPayload, customError);
  }

  public resetPasswordIn$(
    otp: number,
    userId: string,
    newPassword: string,
    customError: boolean = true
  ): Observable<HttpResponse<void> | HttpErrorResponse> {
    const url = `${apiRoutes.RESET_PASSWORD_URL}`;
    const requestPayload: IResetPassword = {
      otp: otp.toString(),
      userId,
      newPassword
    };
    return this.postRequest<void, IResetPassword>(url, requestPayload, customError, (res) => {
      let errorMessage;
      if (res?.status === 500) {
        errorMessage = NotificationConstants.SOMETHING_WENT_WRONG;
        this.notificationService.addNotification(errorMessage, NotificationTypes.ERROR);
      } else {
        this.notificationService.addNotification(
          res.error?.error_description || res.error?.errors?.[0]?.message,
          NotificationTypes.ERROR
        );
      }
    });
  }

  public userRegistrationIn$(
    firstName: string,
    lastName: string,
    email: string,
    password: string,
    aladdinMailing: boolean
  ): Observable<HttpResponse<void> | HttpErrorResponse> {
    const url = `${apiRoutes.REGISTER_USER_URL}`;
    const requestPayload: IUserRegister = {
      firstName,
      lastName,
      uid: email,
      password,
      aladdinMailing
    };
    return this.postRequest<void, IUserRegister>(url, requestPayload);
  }

  public userProfileIn$(email: string): Observable<HttpResponse<IUserProfile> | HttpErrorResponse> {
    const userProfileUrl = `${apiRoutes.USER_PROFILE_URL}/${encodeURIComponent(email)}`;
    return this.httpClient$
      .get<IUserProfile>(userProfileUrl, { observe: HttpClientObserveEnum.RESPONSE })
      .pipe(catchError((error: HttpErrorResponse) => of(error)));
  }

  public getUserProfileCached$(email: string): Observable<HttpResponse<IUserProfile> | HttpErrorResponse> {
    const key = email || '';
    const existing = this.userProfileInFlight.get(key);
    if (existing) return existing;

    const request$ = this.userProfileIn$(email).pipe(
      shareReplay(1),
      finalize(() => this.userProfileInFlight.delete(key))
    );
    this.userProfileInFlight.set(key, request$);
    return request$;
  }

  public updateUserProfileIn$(
    userProfilePayload: IUserProfileUpdate
  ): Observable<HttpResponse<void> | HttpErrorResponse> {
    const userUpdateUrl = `${apiRoutes.USER_PROFILE_URL}/${encodeURIComponent(userProfilePayload?.profileEmail ?? '')}/profile`;
    return this.httpClient$
      .patch<void>(userUpdateUrl, userProfilePayload, { observe: HttpClientObserveEnum.RESPONSE })
      .pipe(catchError((error: HttpErrorResponse) => of(error)));
  }

  public validateEmailId$(email?: string): Observable<boolean> {
    if (!email) return of(false);

    const validateEmailUrl = `${CART_COMMERCIAL}/${encodeURIComponent(email)}`;
    return this.httpClient$.get<void>(validateEmailUrl, { observe: HttpClientObserveEnum.RESPONSE }).pipe(
      map(() => true),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          return this.getAuthTokenApi$().pipe(
            switchMap((res: HttpResponse<ILoginResponse> | HttpErrorResponse) => {
              if (res instanceof HttpResponse)
                return this.httpClient$.get<void>(validateEmailUrl, { observe: HttpClientObserveEnum.RESPONSE }).pipe(
                  map(() => true),
                  catchError(() => of(false))
                );
              else return of(false);
            }),
            catchError(() => of(false))
          );
        }

        return of(false);
      })
    );
  }
}
